import { useState } from 'react';
import { useNavigate } from 'react-router';
import {
  Box, Grid, Typography, Button, CircularProgress,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { JournalCard } from '../../components/journals/JournalCard';
import { JournalCreateModal } from '../../components/journals/JournalCreateModal';
import { useJournals, useCreateJournal } from '../../hooks/useJournals';
import type { CreateJournalPayload } from '../../types/api.types';

export const JournalsPage = () => {
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState(false);
  const { data: journals, isLoading } = useJournals();
  const { mutate: createJournal, isPending } = useCreateJournal();

  const handleCreate = (payload: CreateJournalPayload) => {
    createJournal(payload, {
      onSuccess: (j) => {
        setModalOpen(false);
        navigate(`/journals/${j.id}`);
      },
    });
  };

  if (isLoading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;
  }

  const hasJournals = journals && journals.length > 0;

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">My Journals</Typography>
        {hasJournals && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setModalOpen(true)}>
            New Journal
          </Button>
        )}
      </Box>

      {!hasJournals ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 10, gap: 2 }}>
          <Typography variant="h5" color="text.secondary">Start your first journal</Typography>
          <Typography variant="body2" color="text.secondary">
            Write in the language you're learning and get AI feedback on your progress.
          </Typography>
          <Button variant="contained" size="large" startIcon={<AddIcon />} onClick={() => setModalOpen(true)}>
            Create Journal
          </Button>
        </Box>
      ) : (
        <Grid container spacing={2}>
          {journals.map(j => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={j.id}>
              <JournalCard journal={j} onClick={() => navigate(`/journals/${j.id}`)} />
            </Grid>
          ))}
        </Grid>
      )}

      <JournalCreateModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSubmit={handleCreate}
        isLoading={isPending}
      />
    </Box>
  );
};
